import React from 'react'
import { Link, Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@contexts/AuthContext'
import { usePatientAuth } from '@hooks/usePatientAuth'
import { LogOut, Menu, LayoutDashboard, CalendarDays, User, X, MessageCircle } from 'lucide-react'
import { clsx } from 'clsx'
import ConfirmationModal from '@components/web/ConfirmationModal'
import { appointmentService } from '@lib/supabase/appointment.service'
import { chatService } from '@lib/supabase/chat.service'
import { queryKeys } from '@lib/queryKeys'

const NAV_ITEMS = [
  { to: '/patient/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/patient/chats', label: 'Chats', icon: MessageCircle },
  { to: '/patient/appointments', label: 'Appointments', icon: CalendarDays },
  { to: '/patient/profile', label: 'Profile', icon: User },
]

export default function PatientLayout() {
  const { logout } = useAuth()
  const { patient, refreshPatient } = usePatientAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [sidebarOpen, setSidebarOpen] = React.useState(false)
  const [showSignOutModal, setShowSignOutModal] = React.useState(false)
  const [isSigningOut, setIsSigningOut] = React.useState(false)

  const unreadChatCountQuery = useQuery({
    queryKey: queryKeys.patient.unreadChatCount(patient?.id),
    queryFn: async () => {
      if (!patient?.id) return 0

      const [chatAppointments, latestIncomingMap] = await Promise.all([
        appointmentService.getPatientAppointments(undefined, 'Chat Consultation'),
        chatService.getLatestIncomingProviderMessageMap(patient.id),
      ])

      const unreadKeys = chatService.getUnreadConversationKeys(patient.id, chatAppointments, latestIncomingMap)
      return unreadKeys.size
    },
    enabled: !!patient?.id,
  })

  const unreadChatCount = unreadChatCountQuery.data ?? 0

  React.useEffect(() => {
    const handleProfileUpdated = () => {
      void refreshPatient()
    }

    window.addEventListener('patient-profile-updated', handleProfileUpdated)
    return () => window.removeEventListener('patient-profile-updated', handleProfileUpdated)
  }, [refreshPatient])

  React.useEffect(() => {
    if (!patient?.id) {
      return
    }

    const subscription = chatService.subscribeToPatientIncomingMessages(patient.id, () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.patient.unreadChatCount(patient.id) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.patient.latestIncomingMessages(patient.id) })
    })

    const handleStorage = (event: StorageEvent) => {
      if (event.key === `patient-chat-seen:${patient.id}`) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.patient.unreadChatCount(patient.id) })
      }
    }

    window.addEventListener('storage', handleStorage)

    return () => {
      subscription.unsubscribe()
      window.removeEventListener('storage', handleStorage)
    }
  }, [patient?.id, queryClient])

  const handleLogout = async () => {
    setIsSigningOut(true)
    try {
      await logout()
    } finally {
      navigate('/auth/login?role=patient', { replace: true })
    }
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border flex flex-col',
          'transform transition-transform duration-300 ease-in-out',
          'md:relative md:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="p-5 border-b border-border flex items-center justify-between">
          <Link to="/patient/dashboard" onClick={() => setSidebarOpen(false)} className="inline-flex items-center">
            <img src="/logos.png" alt="HomiCareplus" className="h-7 w-auto" />
          </Link>
          <button title="Close sidebar" className="md:hidden p-1 hover:bg-muted rounded" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2.5 border text-sm font-medium transition-all',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={18} />
                  <span className="flex-1">{label}</span>
                  {to === '/patient/chats' && unreadChatCount > 0 ? (
                    <span className={clsx(
                      'min-w-5 rounded-full px-1.5 py-0.5 text-[11px] font-bold leading-none text-center',
                      isActive ? 'bg-primary-foreground/15 text-primary-foreground' : 'bg-sky-100 text-sky-700',
                    )}>
                      {unreadChatCount > 9 ? '9+' : unreadChatCount}
                    </span>
                  ) : null}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Sidebar footer */}
        <div className="p-4 border-t border-border space-y-3">
          <div className="overflow-hidden bg-transparent">
            <img
              src="/Sidebanner0.png"
              alt="Zero paperwork burden banner"
              className="w-full h-auto object-cover"
            />
          </div>

          <div>
            <button
              onClick={() => setShowSignOutModal(true)}
              disabled={isSigningOut}
              className="w-full flex items-center gap-3 px-3 py-2.5 border text-sm font-medium text-destructive hover:bg-destructive/10 transition-all"
            >
              {isSigningOut ? (
                <div className="h-[18px] w-[18px] rounded-full border-2 border-destructive border-t-transparent animate-spin" />
              ) : (
                <LogOut size={18} />
              )}
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="bg-card border-b border-border px-4 py-3 flex items-center justify-between md:hidden shrink-0">
          <Link to="/patient/dashboard" onClick={() => setSidebarOpen(false)} className="inline-flex items-center">
            <img src="/logos.png" alt="HomiCareplus" className="h-7 w-auto" />
          </Link>
          <button
            title="Open sidebar"
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-muted border"
          >
            <Menu size={22} />
          </button>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </main>

      {showSignOutModal && (
        <ConfirmationModal
          title="Sign out?"
          message="You will need to sign in again to access your patient dashboard."
          confirmLabel="Sign Out"
          confirmVariant="destructive"
          isLoading={isSigningOut}
          onConfirm={handleLogout}
          onCancel={() => {
            if (!isSigningOut) {
              setShowSignOutModal(false)
            }
          }}
        />
      )}
    </div>
  )
}
